import type { PlainWorkflow, PlainWorkflowExecution } from "@/queries/automation-queries";
import { useMemo, useState, type FC } from "react";
import { Link } from "@/components/flexkit/Link";
import { WorkflowExecutionsCard } from "@/components/automations/AutomationExecutionCard";
import { hasFailedActions, workflowExecutionsUrl, getWorkflowTriggerDescription, isWorkflowDeleting } from "@/utils/automations";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { Button } from "@/components/flexkit/Button";
import { Command, CommandEmpty, CommandInput, CommandList } from "@/components/flexkit/Command";
import { Command as CommandPrimitive } from "cmdk";
import { standaloneInputClassName } from "@/components/podkit/forms/input-classes";
import { cn } from "@/components/podkit/lib/cn";
import {
    WorkflowExecutionStatusFilter,
    WorkflowExecutionStatusFilterValue,
} from "@/components/automations/AutomationExecutionStatusFilter";
import { WorkflowExecutionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { OrganizationMember } from "gitpod-next-api/gitpod/v1/organization_pb";
import { formatError } from "@/utils/errors";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { RunAutomationButton } from "@/routes/automations/automation-details-page/RunAutomationButton";

type WorkflowExecutionsListProps = {
    executions: PlainWorkflowExecution[];
    members?: PlainMessage<OrganizationMember>[];
    hasManualTrigger: boolean;
    workflow?: PlainWorkflow;
    error?: Error | null;
    onRetry?: () => void;
};

export const WorkflowExecutionsList: FC<WorkflowExecutionsListProps> = ({
    executions,
    members,
    hasManualTrigger,
    workflow,
    error,
    onRetry,
}) => {
    const [searchValue, setSearchValue] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<WorkflowExecutionStatusFilterValue>(
        WorkflowExecutionStatusFilterValue.All,
    );
    const isDeleting = isWorkflowDeleting(workflow);

    const filteredExecutions = useMemo(() => {
        return executions.filter((execution) => {
            // Apply search filter
            if (searchValue && !execution.id.toLowerCase().includes(searchValue.toLowerCase())) {
                return false;
            }

            // Apply status filter
            if (statusFilter === WorkflowExecutionStatusFilterValue.All) {
                return true;
            }

            const phase = execution.status?.phase;
            if (statusFilter === WorkflowExecutionStatusFilterValue.Running) {
                return (
                    phase === WorkflowExecutionPhase.RUNNING ||
                    phase === WorkflowExecutionPhase.PENDING ||
                    phase === WorkflowExecutionPhase.STOPPING
                );
            }

            if (statusFilter === WorkflowExecutionStatusFilterValue.Completed) {
                return phase === WorkflowExecutionPhase.COMPLETED && !hasFailedActions(execution);
            }

            if (statusFilter === WorkflowExecutionStatusFilterValue.Failed) {
                return phase === WorkflowExecutionPhase.COMPLETED && hasFailedActions(execution);
            }

            return true;
        });
    }, [executions, searchValue, statusFilter]);

    if (executions.length === 0) {
        if (error) {
            return (
                <EmptyStateCard
                    title="Failed to load executions"
                    description={formatError(error)}
                    data-testid="workflow-executions-error-state"
                >
                    {onRetry && (
                        <Button
                            variant="secondary"
                            LeadingIcon={IconRefresh}
                            onClick={onRetry}
                            data-tracking-id="retry-load-executions"
                        >
                            Retry
                        </Button>
                    )}
                </EmptyStateCard>
            );
        }

        const triggerDescription = getWorkflowTriggerDescription(workflow);
        const description = hasManualTrigger
            ? "Run this automation to create a new execution. It will run all defined steps across your configured contexts and report progress as it completes."
            : `${triggerDescription} It will run all defined steps across your configured contexts and report progress as it completes.`;

        return (
            <EmptyStateCard
                title="No executions yet"
                description={description}
                data-testid="workflow-executions-empty-state"
            >
                {hasManualTrigger && workflow && (
                    <RunAutomationButton
                        workflow={workflow}
                        disabled={isDeleting}
                        data-tracking-id="run-workflow-empty-state"
                    >
                        Run Automation
                    </RunAutomationButton>
                )}
            </EmptyStateCard>
        );
    }

    return (
        <Command
            className="flex-col gap-3 overflow-visible border-none bg-transparent shadow-none"
            data-testid="workflow-executions-list"
        >
            <div className="flex items-center justify-between gap-3">
                <CommandInput
                    placeholder="Search executions"
                    value={searchValue}
                    onValueChange={setSearchValue}
                    containerClassName={cn(
                        standaloneInputClassName,
                        "max-w-none *:p-0",
                        "read-only:bg-inherit read-only:cursor-auto read-only:text-inherit",
                    )}
                />
                <WorkflowExecutionStatusFilter value={statusFilter} onChange={setStatusFilter} />
            </div>
            <CommandList defaultChecked={false} className="px-1 py-1 *:flex *:flex-col *:gap-2">
                <CommandEmpty className="w-full text-center text-base text-content-muted">
                    No executions found
                    {searchValue && (
                        <>
                            {" "}
                            matching <span className="font-medium text-content-strong">{searchValue}</span>
                        </>
                    )}
                </CommandEmpty>
                {filteredExecutions.map((execution) => (
                    <CommandPrimitive.Item key={execution.id} value={execution.id} className="group" asChild>
                        <Link to={workflowExecutionsUrl(execution)} className="flex-1">
                            <WorkflowExecutionsCard execution={execution} members={members} />
                        </Link>
                    </CommandPrimitive.Item>
                ))}
            </CommandList>
        </Command>
    );
};
